import { useState } from 'react'
import Button from '@components/atoms/button'
import { CompetitionTimer } from '@components/competitions/competition-timer'
import { useSessionContext } from '@contexts/session'
import { CALENDAR_IMAGE_PATH } from '@lib/constants/teacher-dashboard'
import { FastlyImage } from '@components/common/image'

type Props = {
  start: string
  onYesClick: () => void
  onNoClick: () => void
  lessonNumber: string | number
  lessonTitle: string
  rescheduleRequestedBy: 'student' | 'teacher' | null
  setRescheduleRequestedBy: (value: 'student' | 'teacher' | null) => void
}

const studentConfirmations = {
  'student-confirm1':
    'I confirm the student/parent has informed me to reschedule this class.',
  'student-confirm2':
    'I confirm a discussion about this occurred on the WhatsApp group.',
  'student-confirm3':
    "I've informed students they can only cancel up to two classes monthly and should do so only in emergencies.",
  'student-confirm4':
    'I understand an automated message will be sent to the student: "Your teacher informed us that you have requested to cancel your upcoming class."',
  'student-confirm5':
    'I confirm I am not exploiting the system, and I understand penalties may apply for any wrongdoing.',
}

const teacherConfirmations = {
  'teacher-confirm1':
    'I have notified them about this cancellation on WhatsApp group to students & parents.',
  'teacher-confirm2':
    "I understand the impact on the student's learning journey and will try to provide a substitute class with another teacher or myself as soon as possible.",
  'teacher-confirm3':
    'I know the class cancellation policy: penalties range from INR 300 to INR 500 per class, and frequent teacher cancellations may lead to strict action from Codingal.',
}

const CheckIfStudentIsInformed = ({
  start,
  onNoClick,
  onYesClick,
  lessonNumber,
  lessonTitle,
  rescheduleRequestedBy,
  setRescheduleRequestedBy,
}: Props) => {
  const { name } = useSessionContext()
  const [confirmations, setConfirmations] = useState({
    student: Object.keys(studentConfirmations).map(() => false),
    teacher: Object.keys(teacherConfirmations).map(() => false),
  })

  const handleConfirmationChange = (
    type: 'student' | 'teacher',
    index: number
  ) => {
    setConfirmations((prev) => {
      const newConfirmations = { ...prev }
      newConfirmations[type][index] = !newConfirmations[type][index]
      return newConfirmations
    })
  }

  const allConfirmationsChecked = (type: 'student' | 'teacher') => {
    return confirmations[type].every((item) => item)
  }

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex items-center justify-center">
        <FastlyImage
          src={`${CALENDAR_IMAGE_PATH}calendar-reschedule.png`}
          width={60}
          height={60}
        />
      </div>
      <div className="space-y-2">
        <p className="text-lg leading-tight font-600">
          You are rescheduling Lesson{lessonNumber && ` ${lessonNumber}`}:{' '}
          {lessonTitle}
        </p>
        <p className="leading-tight text-grey-800">
          Hey {name},{' '}
          <span className="font-600">
            {' '}
            only <CompetitionTimer endTime={start} className="font-600" /> left
          </span>{' '}
          for the class to start. Class cancellation might degrade the
          student&apos;s experience.
        </p>
      </div>
      <div className="space-y-3">
        <p className="text-lg leading-tight font-600 -mt-px">
          Who requested to reschedule this class?
        </p>
        <div className="space-y-2">
          <div className="flex space-x-2">
            <input
              type="radio"
              id="student"
              name="rescheduleBy"
              value="student"
              checked={rescheduleRequestedBy === 'student'}
              onChange={() => setRescheduleRequestedBy('student')}
            />
            <label
              htmlFor="student"
              className={`${
                rescheduleRequestedBy === 'student' ? 'font-600' : ''
              }`}
            >
              Student requested to reschedule
            </label>
          </div>
          {rescheduleRequestedBy === 'student' && (
            <div
              className="pl-6 space-y-2 thin-scroll-bar"
              style={{ maxHeight: '195px', overflowY: 'auto' }}
            >
              {Object.entries(studentConfirmations).map(([id, text], index) => (
                <div key={id} className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id={id}
                    checked={confirmations.student[index]}
                    onChange={() => handleConfirmationChange('student', index)}
                    className="form-checkbox cursor-pointer h-4 w-4 mt-1"
                  />
                  <label htmlFor={id} className="leading-tight">
                    {text}
                  </label>
                </div>
              ))}
            </div>
          )}
          <div
            className={`flex space-x-2 ${
              rescheduleRequestedBy === 'student' ? 'pt-2' : ''
            }`}
          >
            <input
              type="radio"
              id="teacher"
              name="rescheduleBy"
              value="teacher"
              checked={rescheduleRequestedBy === 'teacher'}
              onChange={() => setRescheduleRequestedBy('teacher')}
            />
            <label
              htmlFor="teacher"
              className={rescheduleRequestedBy === 'teacher' ? 'font-600' : ''}
            >
              Teacher requested to reschedule
            </label>
          </div>
          {rescheduleRequestedBy === 'teacher' && (
            <div
              className="pl-6 space-y-2 thin-scroll-bar"
              style={{ maxHeight: '195px', overflowY: 'auto' }}
            >
              {Object.entries(teacherConfirmations).map(([id, text], index) => (
                <div key={id} className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id={id}
                    checked={confirmations.teacher[index]}
                    onChange={() => handleConfirmationChange('teacher', index)}
                    className="form-checkbox cursor-pointer h-4 w-4 mt-1"
                  />
                  <label htmlFor={id}>{text}</label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-xl leading-tight font-600">
          Is the student aware of this rescheduling?
        </p>
        <div className="flex space-x-5">
          <Button
            onClick={onYesClick}
            disabled={
              !rescheduleRequestedBy ||
              !allConfirmationsChecked(rescheduleRequestedBy)
            }
            className="text-sm"
          >
            Yes, the student is aware
          </Button>
          <button
            onClick={onNoClick}
            className="font-600 text-orange hover:underline focus:outline-none text-sm"
          >
            No, student is not aware
          </button>
        </div>
      </div>
    </div>
  )
}

export default CheckIfStudentIsInformed
